import api from '../axiosInstance';
import { getSelectedCompanyCurrencyCode } from './companyCurrenciesService';

export async function fetchCompany(companyId = null) {
  try {
    const id = companyId || localStorage.getItem('companyId') || 1;

    const response = await api.get(`Company/edit/${id}`, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const { configuration, parametricData } = response.data;

    const currencyItem = parametricData?.currencies?.find(
      (c) => c.id === configuration.companyCurrencyId
    );
    const currency = currencyItem?.currency;
    const timeZone = parametricData?.timeZones?.find(
      (t) => t.id === configuration.timeZoneSettingId
    );
    const language = parametricData?.languages?.find(
      (l) => l.id === configuration.languageId
    );
    const decimalSep = parametricData?.decimalSeparators?.find(
      (d) => d.id === configuration.decimalSeparatorId
    );
    const thousandSep = parametricData?.thousandSeparators?.find(
      (t) => t.id === configuration.thousandSeparatorId
    );

    const [branchesResponse, selectedCurrencyCode] = await Promise.all([
      api.get(`Company/${id}/branches`),
      getSelectedCompanyCurrencyCode(),
    ]);

    const timezoneMap = {
      Chile: 'America/Santiago',
      Argentina: 'America/Argentina/Buenos_Aires',
      Brasil: 'America/Sao_Paulo',
      España: 'Europe/Madrid',
      México: 'America/Mexico_City',
    };

    const timezoneLabel = timezoneMap[timeZone?.name] || 'America/Santiago';

    const finalData = {
      id: configuration.id,
      name: 'Mi empresa',
      regionalConfig: {
        currency: {
          name: currencyItem?.customName || currency?.name || '',
          symbol: currency?.symbol || '',
          iso:
            currencyItem?.code ||
            currency?.code ||
            currency?.iso ||
            selectedCurrencyCode ||
            '',
        },
        timezone: {
          label: timezoneLabel,
          utcOffsets: ['-4'],
        },
        language: language?.name || '',
        numberFormat: {
          thousands: thousandSep?.symbol || '.',
          decimal: decimalSep?.symbol || ',',
          example: `1${thousandSep?.symbol || '.'}234${decimalSep?.symbol || ','}56`,
        },
      },
      branches: branchesResponse.data || [],
      _originalIds: {
        currencyId: configuration.companyCurrencyId,
        timeZoneSettingId: configuration.timeZoneSettingId,
        languageId: configuration.languageId,
        decimalSeparatorId: configuration.decimalSeparatorId,
        thousandSeparatorId: configuration.thousandSeparatorId,
      },
      _rawCurrencies: parametricData?.currencies || [],
      _parametricData: {
        ...parametricData,
        currencies: (parametricData?.currencies || []).map((c) => ({
          id: c.id,
          name: c.customName || c.currency?.name || '',
          symbol: c.currency?.symbol || '',
          iso: c.code || c.currency?.code || c.currency?.iso || '',
        })),
      },
    };

    return finalData;
  } catch (error) {
    console.error('fetchCompany failed:', error);
    console.error('Error details:', error.response?.data);

    return {
      id: '',
      name: '',
      regionalConfig: {
        currency: { name: '', symbol: '', iso: '' },
        timezone: { label: 'America/Santiago', utcOffsets: [] },
        language: '',
        numberFormat: { thousands: '.', decimal: ',', example: '' },
      },
      branches: [],
    };
  }
}

export async function updateCompany(company) {
  try {
    if (!company?.id) throw new Error('Company ID is missing.');

    const payload = {
      companyCurrencyId: company._originalIds?.currencyId,
      timeZoneSettingId: company._originalIds?.timeZoneSettingId,
      languageId: company._originalIds?.languageId,
      decimalSeparatorId: company._originalIds?.decimalSeparatorId,
      thousandSeparatorId: company._originalIds?.thousandSeparatorId,
    };

    const response = await api.put(`Company/${company.id}`, payload, {
      headers: { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    console.error('updateCompany failed:', error);
    throw error;
  }
}

/**
 * Crea una nueva sucursal para una empresa
 * @param {number} companyId - ID de la empresa
 * @param {object} branchData - Datos de la sucursal { name, address, phone, email, status }
 * @returns {Promise} - La sucursal creada
 */
export async function createBranch(companyId, branchData) {
  try {
    if (!companyId) throw new Error('Company ID is required');

    const payload = {
      name: branchData.name || '',
      address: branchData.address || '',
      phone: branchData.phone || '',
      email: branchData.email || '',
      status: branchData.status || 'Active',
    };

    const response = await api.post(`Company/${companyId}/branches`, payload, {
      headers: { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    console.error('createBranch failed:', error);
    console.error('Error details:', error.response?.data);
    throw error;
  }
}

/**
 * Actualiza una sucursal existente
 * @param {number} branchId - ID de la sucursal
 * @param {object} branchData - Datos actualizados
 * @param {number} companyId - ID de la empresa (opcional, para validación)
 * @returns {Promise} - La sucursal actualizada
 */
export async function updateBranch(branchId, branchData, companyId = null) {
  try {
    if (!branchId) {
      throw new Error('Branch ID is required');
    }

    const payload = {
      id: branchId,
      name: branchData.name || '',
      address: branchData.address || '',
      phone: branchData.phone || '',
      email: branchData.email || '',
      status: branchData.status || 'Active',
      companyId: companyId || branchData.companyId || 0,
    };

    const response = await api.put(`Company/branches/${branchId}`, payload, {
      headers: { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    console.error('updateBranch failed:', error);
    console.error('Error details:', error.response?.data);
    throw error;
  }
}

/**
 * Elimina una sucursal (borrado lógico)
 * @param {number} branchId - ID de la sucursal a eliminar
 * @returns {Promise}
 */
export async function deleteBranch(branchId) {
  try {
    if (!branchId) {
      throw new Error('Branch ID is required');
    }

    const response = await api.delete(`Company/branches/${branchId}`, {
      headers: { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    console.error('deleteBranch failed:', error);
    console.error('Error details:', error.response?.data);
    throw error;
  }
}

export async function getAllBranch(companyId = 1) {
  try {
    if (!companyId) throw new Error('Company ID is required');

    const response = await api.get(`Company/${companyId}/branches`, {
      headers: { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    console.error('createBranch failed:', error);
    console.error('Error details:', error.response?.data);
    throw error;
  }
}
